import React, { useState, useEffect, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Calendar,
  Trash2,
  Clock,
  User,
  MapPin,
  Check,
  X,
  Plus,
  ListTodo,
  Edit2,
  Syringe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { VaccineCombobox } from "@/components/Scheduler/schedule/VaccineComboBox";
import useTitanApp from "@/hooks/useTitanApp";
import { getCompanyVaccineByCompanyId } from "@/dispatchers/nmsDispatchers";
import {
  useScheduleData,
  UseScheduleDataReturn,
} from "@/components/Scheduler/scheduleData";
import type { TreatmentFollowUpAppointment } from "@/lib/treatmentPlan/treatmentPlanData";

// ─────────────────────────────────────────────────────────────────────────────
// Internal form shape (richer than the stored shape — has Task[] / Vaccine[])
// ─────────────────────────────────────────────────────────────────────────────
interface Task {
  id: string;
  text: string;
  completed?: boolean;
}

interface Vaccine {
  id: string;
  name: string;
  dose?: string;
  notes?: string;
}

interface AppointmentFormData {
  id: string;
  serviceType: string;
  serviceId: string;
  date: string;
  time: string;
  pharmacistId: string;
  pharmacist: string;
  duration: string;
  roomId: string;
  room: string;
  notes: string;
  tasks: Task[];
  vaccines: Vaccine[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Conversion helpers between internal form shape and stored shape
// ─────────────────────────────────────────────────────────────────────────────

/** Internal AppointmentFormData → TreatmentFollowUpAppointment (stored in parent) */
const toStoredAppt = (
  data: AppointmentFormData,
): TreatmentFollowUpAppointment => ({
  id: data.id,
  serviceId: data.serviceId,
  serviceType: data.serviceType,
  date: data.date,
  time: data.time,
  duration: data.duration,
  resourceId: data.pharmacistId,
  pharmacist: data.pharmacist,
  roomId: data.roomId,
  room: data.room,
  notes: data.notes,
  tasksToComplete: data.tasks.map((t) => t.text),
  vaccinesAdminister: data.vaccines.map((v) => ({
    id: v.id,
    name: v.name,
    vaccineName: v.name,
    dose: v.dose,
    notes: v.notes,
  })),
});

const getStoredDateTimeParts = (value?: string) => {
  const match = String(value || "").match(
    /^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/,
  );
  return {
    date: match?.[1] || "",
    time: match?.[2] || "",
  };
};

/** TreatmentFollowUpAppointment (from parent) → internal AppointmentFormData */
const fromStoredAppt = (
  appt: TreatmentFollowUpAppointment,
): AppointmentFormData => {
  const startDateParts = getStoredDateTimeParts(appt.startDate);

  // Ensure we have a date and time - prefer startDate extraction, but ensure we have values
  const dateValue = startDateParts.date || appt.date || "";
  let timeValue = startDateParts.time || appt.time || "";

  // If we don't have time but have startDate, try to extract it more thoroughly
  if (!timeValue && appt.startDate) {
    try {
      // Try splitting on T and extracting HH:MM
      const parts = String(appt.startDate).split("T");
      if (parts[1]) {
        timeValue = parts[1].substring(0, 5); // Gets "HH:MM"
      }
    } catch {}
  }

  return {
    id: appt.id || uuidv4(),
    serviceType:
      appt.serviceType ||
      (appt as any).serviceName ||
      (appt as any).service ||
      "",
    serviceId: appt.serviceId || "",
    date: dateValue,
    time: timeValue,
    pharmacistId: appt.resourceId || "",
    pharmacist:
      appt.pharmacist ||
      (appt as any).pharmacistName ||
      (appt as any).resourceName ||
      "",
    duration: String(appt.duration || "30"),
    roomId: appt.roomId || "",
    room: appt.room || (appt as any).roomName || "",
    notes: appt.notes || "",
    tasks: (appt.tasksToComplete || []).map((text) => ({
      id: uuidv4(),
      text,
      completed: false,
    })),
    vaccines: (appt.vaccinesAdminister || []).map((v: any) => ({
      id: v.id || uuidv4(),
      name: v.name || v.vaccineName || "",
      dose: v.dose,
      notes: v.notes,
    })),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface FollowUpSectionProps {
  /** Full appointment objects — no longer bare IDs */
  followUpAppointments: TreatmentFollowUpAppointment[];
  addFollowUpAppointment: (appt: TreatmentFollowUpAppointment) => void;
  removeFollowUpAppointment: (appointmentId: string) => void;
  /** Called when an existing appointment's tasks / vaccines / details change */
  updateFollowUpAppointment?: (
    id: string,
    updates: Partial<TreatmentFollowUpAppointment>,
  ) => void;
  /** Legacy props kept so callers don't break — not used internally anymore */
  schedulePharmacists?: any[];
  scheduleRoomNames?: string[];
  isDarkMode: boolean;
  isAdding?: boolean;
  setIsAdding?: (isAdding: boolean) => void;
  services?: any[];
  /** When true, confirms and calls API before removing from array */
  editMode?: boolean;
  /** Called in edit mode with the appointment id — handler performs API delete + plan update */
  onDeleteInEditMode?: (apptId: string) => Promise<void>;
  pendingAppointment?: { notes: string; duration: string };
  consultationStatus?: number;
  stateConsultationId?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
const getTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const EMPTY_NEW_APPT: AppointmentFormData = {
  id: "",
  serviceType: "",
  serviceId: "",
  date: "",
  time: "",
  pharmacistId: "",
  pharmacist: "",
  duration: "30",
  roomId: "",
  room: "",
  notes: "",
  tasks: [],
  vaccines: [],
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const FollowUpSection: React.FC<FollowUpSectionProps> = ({
  followUpAppointments,
  addFollowUpAppointment,
  removeFollowUpAppointment,
  updateFollowUpAppointment,
  isDarkMode,
  isAdding = false,
  setIsAdding = () => {},
  editMode = false,
  onDeleteInEditMode,
  pendingAppointment,
  consultationStatus,
  stateConsultationId,
}) => {
  // ── schedule cascade hook (same as BookingModal) ──────────────────────────
  const s = useScheduleData();
  const {
    servicePharmacists = [],
    serviceTypesInfo = [],
    serviceRooms = [],
    serviceDurations = [],
    availableDates = [],
    getAvailableTimesForDate,
    getAggregatedTimesForDate,
    onServiceTypeSelected,
    onPharmacistSelected,
    handleGetAppointmentRooms,
    isEventCalendarLoading = false,
  } = (s || {}) as Partial<UseScheduleDataReturn>;

  // ── vaccines ──────────────────────────────────────────────────────────────
  const { appState, nmsServiceState } = useTitanApp();
  const { companyId } = appState || {};
  const { companyVaccines } = nmsServiceState || {};
  useEffect(() => {
    if (isAdding && pendingAppointment) {
      setNewAppointment((prev) => ({
        ...prev,
        notes: pendingAppointment.notes || "",
        duration: pendingAppointment.duration || "30",
      }));
    }
  }, [isAdding, pendingAppointment]);

  useEffect(() => {
    if (companyId) getCompanyVaccineByCompanyId(companyId).catch(() => {});
  }, [companyId]);

  const flattenedProducts = useMemo(() => {
    const results: any[] = [];
    const seenIds = new Set();
    (companyVaccines || []).forEach((item: any) => {
      const vaccine =
        item?.value && typeof item.value === "object" ? item.value : item;
      if (vaccine?.vaccineName) {
        (vaccine.products || []).forEach((product: any) => {
          const uniqueId = `${vaccine._id}-${product.productId}`;
          if (!seenIds.has(uniqueId)) {
            results.push({ ...vaccine, selectedProduct: product, uniqueId });
            seenIds.add(uniqueId);
          }
        });
      }
    });
    return results;
  }, [companyVaccines]);

  // ── NEW appointment form state ────────────────────────────────────────────
  const [newAppointment, setNewAppointment] = useState<AppointmentFormData>({
    ...EMPTY_NEW_APPT,
  });

  // ── EDIT appointment state ────────────────────────────────────────────────
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingAppt, setEditingAppt] = useState<AppointmentFormData | null>(
    null,
  );

  // ── task / vaccine UI state ───────────────────────────────────────────────
  const [addingTaskFor, setAddingTaskFor] = useState<string | null>(null);
  const [newTaskText, setNewTaskText] = useState("");
  const [vaccineInputValue, setVaccineInputValue] = useState("");
  const [vaccineModalFor, setVaccineModalFor] = useState<string | null>(null);
  const [addingTaskToNew, setAddingTaskToNew] = useState(false);
  const [newAppointmentTaskText, setNewAppointmentTaskText] = useState("");
  const [addingVaccineToNew, setAddingVaccineToNew] = useState(false);
  const [newAppointmentVaccineId, setNewAppointmentVaccineId] = useState("");
  const [newApptDateError, setNewApptDateError] = useState("");
  const [editApptDateError, setEditApptDateError] = useState("");

  const todayStr = getTodayStr();

  const durationOptions = serviceDurations?.length
    ? serviceDurations
    : [
        { value: "15", label: "15 minutes" },
        { value: "30", label: "30 minutes" },
        { value: "45", label: "45 minutes" },
        { value: "60", label: "60 minutes" },
      ];

  // ─────────────────────────────────────────────────────────────────────────
  // Available times
  // ─────────────────────────────────────────────────────────────────────────
  const newApptAvailableTimes = useMemo(() => {
    if (!newAppointment.date) return [];
    if (newAppointment.pharmacistId && getAvailableTimesForDate)
      return getAvailableTimesForDate(newAppointment.date);
    if (getAggregatedTimesForDate)
      return getAggregatedTimesForDate(
        newAppointment.date,
        parseInt(newAppointment.duration || "30", 10),
      );
    return [];
  }, [
    newAppointment.date,
    newAppointment.pharmacistId,
    newAppointment.duration,
    getAvailableTimesForDate,
    getAggregatedTimesForDate,
  ]);

  const editApptAvailableTimes = useMemo(() => {
    if (!editingAppt?.date) return [];
    const times =
      editingAppt.pharmacistId && getAvailableTimesForDate
        ? getAvailableTimesForDate(editingAppt.date)
        : [];

    if (editingAppt.time && !times.includes(editingAppt.time)) {
      return [...times, editingAppt.time].sort();
    }

    return times;
  }, [
    editingAppt?.date,
    editingAppt?.pharmacistId,
    editingAppt?.time,
    getAvailableTimesForDate,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // CASCADE HANDLERS – NEW APPOINTMENT
  // ═══════════════════════════════════════════════════════════════════════════

  const handleNewApptServiceChange = async (serviceId: string) => {
    const svc = (serviceTypesInfo || []).find((s: any) => s._id === serviceId);
    setNewApptDateError("");
    setNewAppointment((prev) => ({
      ...prev,
      serviceId,
      serviceType: svc?.name || serviceId,
      pharmacistId: "",
      pharmacist: "",
      date: "",
      time: "",
      roomId: "",
      room: "",
      duration: svc?.serviceDuration
        ? String(svc.serviceDuration)
        : prev.duration,
    }));
    await onServiceTypeSelected?.(serviceId);
  };

  const handleNewApptPharmacistChange = async (pharmacistId: string) => {
    const found = servicePharmacists.find((p: any) => p.id === pharmacistId);
    setNewApptDateError("");
    setNewAppointment((prev) => ({
      ...prev,
      pharmacistId,
      pharmacist: found?.name || pharmacistId,
      date: "",
      time: "",
      roomId: "",
      room: "",
    }));
    if (onPharmacistSelected && newAppointment.serviceId)
      await onPharmacistSelected(newAppointment.serviceId, pharmacistId);
  };

  const handleNewApptDateChange = (date: string) => {
    if (
      newAppointment.pharmacistId &&
      availableDates.length > 0 &&
      !availableDates.includes(date)
    ) {
      setNewApptDateError(
        "Pharmacist or Service is not available on this date.",
      );
      return;
    }
    setNewApptDateError("");
    setNewAppointment((prev) => ({
      ...prev,
      date,
      time: "",
      roomId: "",
      room: "",
    }));
  };

  const handleNewApptTimeChange = async (time: string) => {
    setNewAppointment((prev) => ({ ...prev, time, roomId: "", room: "" }));
    if (
      handleGetAppointmentRooms &&
      newAppointment.serviceId &&
      newAppointment.date
    )
      await handleGetAppointmentRooms({
        serviceType: newAppointment.serviceId,
        date: newAppointment.date,
        time,
        duration: newAppointment.duration,
        isNewAppointment: true,
      });
  };

  const handleNewApptRoomChange = (roomId: string) => {
    const found = (serviceRooms as any[]).find((r: any) => r.id === roomId);
    setNewAppointment((prev) => ({
      ...prev,
      roomId,
      room: found?.name || roomId,
    }));
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CASCADE HANDLERS – EDIT APPOINTMENT
  // ═══════════════════════════════════════════════════════════════════════════

  const updateEdit = (field: keyof AppointmentFormData, value: any) =>
    setEditingAppt((prev) => (prev ? { ...prev, [field]: value } : null));

  const handleEditServiceChange = async (serviceId: string) => {
    const svc = (serviceTypesInfo || []).find((s: any) => s._id === serviceId);
    setEditApptDateError("");
    setEditingAppt((prev) =>
      prev
        ? {
            ...prev,
            serviceId,
            serviceType: svc?.name || serviceId,
            pharmacistId: "",
            pharmacist: "",
            date: "",
            time: "",
            roomId: "",
            room: "",
          }
        : null,
    );
    await onServiceTypeSelected?.(serviceId);
  };

  const handleEditPharmacistChange = async (pharmacistId: string) => {
    const found = servicePharmacists.find((p: any) => p.id === pharmacistId);
    setEditApptDateError("");
    setEditingAppt((prev) =>
      prev
        ? {
            ...prev,
            pharmacistId,
            pharmacist: found?.name || pharmacistId,
            date: "",
            time: "",
            roomId: "",
            room: "",
          }
        : null,
    );
    const svcId = editingAppt?.serviceId || "";
    if (onPharmacistSelected && svcId)
      await onPharmacistSelected(svcId, pharmacistId);
  };

  const handleEditDateChange = (date: string) => {
    if (
      editingAppt?.pharmacistId &&
      availableDates.length > 0 &&
      !availableDates.includes(date)
    ) {
      setEditApptDateError(
        "Pharmacist or Service is not available on this date.",
      );
      return;
    }
    setEditApptDateError("");
    setEditingAppt((prev) =>
      prev ? { ...prev, date, time: "", roomId: "", room: "" } : null,
    );
  };

  const handleEditTimeChange = async (time: string) => {
    setEditingAppt((prev) =>
      prev ? { ...prev, time, roomId: "", room: "" } : null,
    );
    const svcId = editingAppt?.serviceId || "";
    const date = editingAppt?.date || "";
    const duration = editingAppt?.duration || "30";
    if (handleGetAppointmentRooms && svcId && date)
      await handleGetAppointmentRooms({
        serviceType: svcId,
        date,
        time,
        duration,
        isNewAppointment: false,
      });
  };

  const handleEditRoomChange = (roomId: string) => {
    const found = (serviceRooms as any[]).find((r: any) => r.id === roomId);
    setEditingAppt((prev) =>
      prev ? { ...prev, roomId, room: found?.name || roomId } : null,
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ADD / CANCEL / SAVE EDIT
  // ═══════════════════════════════════════════════════════════════════════════

  const handleAddAppointment = () => {
    if (
      !newAppointment.serviceId ||
      !newAppointment.date ||
      !newAppointment.time ||
      !newAppointment.pharmacistId
    )
      return;

    const apptWithId: AppointmentFormData = { ...newAppointment, id: uuidv4() };
    // Convert to the stored shape and push to parent
    addFollowUpAppointment(toStoredAppt(apptWithId));
    setNewAppointment({ ...EMPTY_NEW_APPT });
    setNewApptDateError("");
    setAddingTaskToNew(false);
    setNewAppointmentTaskText("");
    setIsAdding(false);
  };

  const handleCancelAdd = () => {
    setNewAppointment({ ...EMPTY_NEW_APPT });
    setNewApptDateError("");
    setAddingTaskToNew(false);
    setNewAppointmentTaskText("");
    setIsAdding(false);
  };

  const handleStartEdit = (appt: TreatmentFollowUpAppointment) => {
    const formAppt = fromStoredAppt(appt);
    setEditingId(appt.id ?? null);
    setEditingAppt(formAppt);
    setEditApptDateError("");

    // Re-trigger cascade so pharmacist/date lists are populated
    if (appt.serviceId) {
      onServiceTypeSelected?.(appt.serviceId);
      if (appt.resourceId) {
        onPharmacistSelected?.(appt.serviceId, appt.resourceId);
      }
    }

    // ── NEW: pre-fetch rooms so the existing roomId can be matched ──
    if (
      handleGetAppointmentRooms &&
      formAppt.serviceId &&
      formAppt.date &&
      formAppt.time
    ) {
      handleGetAppointmentRooms({
        serviceType: formAppt.serviceId,
        date: formAppt.date,
        time: formAppt.time,
        duration: formAppt.duration,
        isNewAppointment: false,
      });
    }
  };

  const handleSaveEdit = () => {
    if (!editingId || !editingAppt) return;
    updateFollowUpAppointment?.(editingId, toStoredAppt(editingAppt));
    setEditingId(null);
    setEditingAppt(null);
    setEditApptDateError("");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingAppt(null);
    setEditApptDateError("");
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // INLINE TASK HELPERS  (operate on parent state via updateFollowUpAppointment)
  // ═══════════════════════════════════════════════════════════════════════════

  const handleAddTaskToNew = () => {
    if (!newAppointmentTaskText.trim()) return;
    setNewAppointment((prev) => ({
      ...prev,
      tasks: [
        ...prev.tasks,
        { id: uuidv4(), text: newAppointmentTaskText.trim(), completed: false },
      ],
    }));
    setNewAppointmentTaskText("");
  };

  const handleRemoveTaskFromNew = (id: string) =>
    setNewAppointment((prev) => ({
      ...prev,
      tasks: prev.tasks.filter((t) => t.id !== id),
    }));

  /** Add task to an already-stored appointment */
  const handleAddTask = (appt: TreatmentFollowUpAppointment) => {
    if (!newTaskText.trim() || !appt.id) return;
    const updated = [...(appt.tasksToComplete || []), newTaskText.trim()];
    updateFollowUpAppointment?.(appt.id, { tasksToComplete: updated });
    setNewTaskText("");
  };

  /** Remove task from an already-stored appointment */
  const handleRemoveTask = (
    appt: TreatmentFollowUpAppointment,
    taskText: string,
  ) => {
    if (!appt.id) return;
    const updated = (appt.tasksToComplete || []).filter((t) => t !== taskText);
    updateFollowUpAppointment?.(appt.id, { tasksToComplete: updated });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // INLINE VACCINE HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  const resolveVaccine = (vaccineId: string): Vaccine => {
    const found = flattenedProducts.find((v: any) => v.uniqueId === vaccineId);
    return {
      id: uuidv4(),
      name: found ? found.vaccineName : vaccineId,
      dose: found?.selectedProduct?.productName,
      notes: found?.selectedProduct?.manufacturerName,
    };
  };

  const handleAddVaccineToNew = (vaccineId: string) => {
    if (!vaccineId) return;
    setNewAppointment((prev) => ({
      ...prev,
      vaccines: [...prev.vaccines, resolveVaccine(vaccineId)],
    }));
    setNewAppointmentVaccineId("");
    setAddingVaccineToNew(false);
  };

  const handleRemoveVaccineFromNew = (id: string) =>
    setNewAppointment((prev) => ({
      ...prev,
      vaccines: prev.vaccines.filter((v) => v.id !== id),
    }));

  const handleVaccineSelect = (
    vaccineId: string,
    appt: TreatmentFollowUpAppointment,
  ) => {
    if (!vaccineId || !appt.id) return;
    const v = resolveVaccine(vaccineId);
    const updated = [
      ...(appt.vaccinesAdminister || []),
      {
        id: v.id,
        name: v.name,
        vaccineName: v.name,
        dose: v.dose,
        notes: v.notes,
      },
    ];
    updateFollowUpAppointment?.(appt.id, { vaccinesAdminister: updated });
    setVaccineInputValue("");
    setVaccineModalFor(null);
  };

  const handleRemoveVaccine = (
    appt: TreatmentFollowUpAppointment,
    vaccineId: string,
  ) => {
    if (!appt.id) return;
    const updated = (appt.vaccinesAdminister || []).filter(
      (v: any) => v.id !== vaccineId,
    );
    updateFollowUpAppointment?.(appt.id, { vaccinesAdminister: updated });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-4">
      {/* ────── ADD NEW APPOINTMENT FORM ────── */}
      {isAdding && (
        <Card
          className={
            isDarkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }
        >
          <CardHeader>
            <CardTitle
              className={`text-base ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
            >
              New Follow-up Appointment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Step 1: Service */}
              <div className="space-y-2">
                <Label
                  className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                >
                  Service Type *
                </Label>
                <Select
                  value={newAppointment.serviceId}
                  onValueChange={handleNewApptServiceChange}
                  disabled={isEventCalendarLoading}
                >
                  <SelectTrigger
                    className={
                      isDarkMode
                        ? "bg-gray-900 border-gray-600 text-gray-200"
                        : ""
                    }
                  >
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {(serviceTypesInfo || [])
                      .filter(
                        (s: any) => s.serviceType !== 1 && s.serviceType !== 3,
                      )
                      .map((svc: any) => (
                        <SelectItem key={svc._id} value={svc._id}>
                          {svc.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Step 2: Pharmacist */}
              <div className="space-y-2">
                <Label
                  className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                >
                  Pharmacist *
                </Label>
                <Select
                  value={newAppointment.pharmacistId}
                  onValueChange={handleNewApptPharmacistChange}
                  disabled={!newAppointment.serviceId || isEventCalendarLoading}
                >
                  <SelectTrigger
                    className={
                      isDarkMode
                        ? "bg-gray-900 border-gray-600 text-gray-200"
                        : ""
                    }
                  >
                    <SelectValue
                      placeholder={
                        newAppointment.serviceId
                          ? "Select pharmacist"
                          : "Select service first"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {servicePharmacists.map((p: any) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Step 3: Date */}
              <div className="space-y-2">
                <Label
                  className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                >
                  Date *
                </Label>
                <Input
                  type="date"
                  value={newAppointment.date}
                  onChange={(e) => handleNewApptDateChange(e.target.value)}
                  min={todayStr}
                  disabled={
                    !newAppointment.pharmacistId || isEventCalendarLoading
                  }
                  className={
                    isDarkMode
                      ? "bg-gray-900 border-gray-600 text-gray-200"
                      : ""
                  }
                />
                {newAppointment.pharmacistId && availableDates.length === 0 && (
                  <p className="text-xs text-amber-500">
                    No available dates for this pharmacist.
                  </p>
                )}
                {!!newApptDateError && (
                  <p className="text-xs text-red-500">{newApptDateError}</p>
                )}
              </div>

              {/* Step 4: Time */}
              <div className="space-y-2">
                <Label
                  className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                >
                  Time *
                </Label>
                <Select
                  value={newAppointment.time}
                  onValueChange={handleNewApptTimeChange}
                  disabled={!newAppointment.date || isEventCalendarLoading}
                >
                  <SelectTrigger
                    className={
                      isDarkMode
                        ? "bg-gray-900 border-gray-600 text-gray-200"
                        : ""
                    }
                  >
                    <SelectValue
                      placeholder={
                        newAppointment.date
                          ? "Select time"
                          : "Select date first"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {newApptAvailableTimes.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Duration */}
              <div className="space-y-2">
                <Label
                  className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                >
                  Duration
                </Label>
                <Select
                  value={newAppointment.duration}
                  onValueChange={(v) =>
                    setNewAppointment((prev) => ({ ...prev, duration: v }))
                  }
                  disabled={isEventCalendarLoading}
                >
                  <SelectTrigger
                    className={
                      isDarkMode
                        ? "bg-gray-900 border-gray-600 text-gray-200"
                        : ""
                    }
                  >
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {durationOptions.map((d: any) => (
                      <SelectItem key={d.value} value={d.value}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Step 5: Room */}
              <div className="space-y-2">
                <Label
                  className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                >
                  Room
                </Label>
                <Select
                  value={newAppointment.roomId || "none"}
                  onValueChange={(v) =>
                    handleNewApptRoomChange(v === "none" ? "" : v)
                  }
                  disabled={!newAppointment.time || isEventCalendarLoading}
                >
                  <SelectTrigger
                    className={
                      isDarkMode
                        ? "bg-gray-900 border-gray-600 text-gray-200"
                        : ""
                    }
                  >
                    <SelectValue
                      placeholder={
                        newAppointment.time
                          ? "Select room (optional)"
                          : "Select time first"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No room assigned</SelectItem>
                    {(serviceRooms as any[]).map((r: any) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
                Notes
              </Label>
              <Textarea
                placeholder="Additional notes..."
                value={newAppointment.notes}
                onChange={(e) =>
                  setNewAppointment((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                className={
                  isDarkMode ? "bg-gray-900 border-gray-600 text-gray-200" : ""
                }
                rows={2}
              />
            </div>

            {/* Tasks for new appointment */}
            <div className="space-y-2 pt-2 border-t border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ListTodo className="w-4 h-4 text-gray-500" />
                  <Label
                    className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                  >
                    Tasks
                  </Label>
                  {newAppointment.tasks.length > 0 && (
                    <Badge
                      variant="outline"
                      className={`text-xs ${isDarkMode ? "border-gray-600 text-gray-400" : ""}`}
                    >
                      {newAppointment.tasks.length}
                    </Badge>
                  )}
                </div>
                {!addingTaskToNew && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setAddingTaskToNew(true)}
                    className={`h-7 text-xs ${isDarkMode ? "text-gray-400 hover:text-gray-300 hover:bg-gray-800" : "text-gray-600"}`}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Task
                  </Button>
                )}
              </div>
              {newAppointment.tasks.map((task) => (
                <div
                  key={task.id}
                  className={`flex items-center justify-between gap-2 p-2 rounded ${isDarkMode ? "bg-gray-900/50" : "bg-gray-50"}`}
                >
                  <span
                    className={`text-sm flex-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    • {task.text}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveTaskFromNew(task.id)}
                    className="h-6 w-6 p-0 text-red-500"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {addingTaskToNew && (
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Type task and press Enter..."
                    value={newAppointmentTaskText}
                    onChange={(e) => setNewAppointmentTaskText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddTaskToNew();
                      } else if (e.key === "Escape") {
                        setAddingTaskToNew(false);
                        setNewAppointmentTaskText("");
                      }
                    }}
                    autoFocus
                    className={`text-sm h-8 ${isDarkMode ? "bg-gray-900 border-gray-600 text-gray-200" : ""}`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setAddingTaskToNew(false);
                      setNewAppointmentTaskText("");
                    }}
                    className="h-8"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>

            {/* Vaccines for new appointment */}
            <div className="space-y-2 pt-2 border-t border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Syringe className="w-4 h-4 text-gray-500" />
                  <Label
                    className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                  >
                    Vaccines
                  </Label>
                  {newAppointment.vaccines.length > 0 && (
                    <Badge
                      variant="outline"
                      className={`text-xs ${isDarkMode ? "border-gray-600 text-gray-400" : ""}`}
                    >
                      {newAppointment.vaccines.length}
                    </Badge>
                  )}
                </div>
                {!addingVaccineToNew && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setAddingVaccineToNew(true)}
                    className={`h-7 text-xs ${isDarkMode ? "text-gray-400 hover:text-gray-300 hover:bg-gray-800" : "text-gray-600"}`}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Vaccine
                  </Button>
                )}
              </div>
              {newAppointment.vaccines.map((v) => (
                <div
                  key={v.id}
                  className={`flex items-start justify-between gap-2 p-2 rounded ${isDarkMode ? "bg-gray-900/50" : "bg-gray-50"}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Syringe className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                      <span
                        className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        {v.name}
                      </span>
                    </div>
                    {v.dose && (
                      <p
                        className={`text-xs ml-5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                      >
                        Dose: {v.dose}
                      </p>
                    )}
                    {v.notes && (
                      <p
                        className={`text-xs ml-5 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                      >
                        {v.notes}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveVaccineFromNew(v.id)}
                    className="h-6 w-6 p-0 text-red-500"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {addingVaccineToNew && (
                <div className="flex items-center gap-2">
                  <VaccineCombobox
                    value={newAppointmentVaccineId}
                    onChange={setNewAppointmentVaccineId}
                    placeholder="Search vaccine..."
                    isDarkMode={isDarkMode}
                    className="flex-1"
                    onSelect={(id) => {
                      setNewAppointmentVaccineId(id);
                      handleAddVaccineToNew(id);
                    }}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      if (newAppointmentVaccineId.trim())
                        handleAddVaccineToNew(newAppointmentVaccineId);
                    }}
                    className="bg-black hover:bg-gray-800 text-white h-8"
                  >
                    <Check className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setAddingVaccineToNew(false);
                      setNewAppointmentVaccineId("");
                    }}
                    className="h-8"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 pt-2">
              <Button
                onClick={handleAddAppointment}
                disabled={
                  !newAppointment.serviceId ||
                  !newAppointment.date ||
                  !newAppointment.time ||
                  !newAppointment.pharmacistId
                }
                className="bg-black hover:bg-gray-800 text-white"
              >
                <Check className="w-4 h-4 mr-2" /> Add Appointment
              </Button>
              <Button
                onClick={handleCancelAdd}
                variant="outline"
                className={
                  isDarkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : ""
                }
              >
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ────── EMPTY STATE ────── */}
      {followUpAppointments.length === 0 && !isAdding ? (
        <Card
          className={
            isDarkMode
              ? "bg-gray-800/50 border-gray-700"
              : "bg-gray-50 border-gray-200"
          }
        >
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar
              className={`w-12 h-12 mb-4 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
            />
            <p
              className={`text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              No follow-up appointments scheduled
            </p>
            <p
              className={`text-sm text-center mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
            >
              Click "+ Add" to schedule a follow-up appointment
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* ── Now iterate full objects, not IDs ── */}
          {followUpAppointments.map((appt, index) => {
            const apptId = appt.id ?? `appt-${index}`;
            const isEditing = editingId === apptId;

            // Compute pharmacist display name with fallbacks
            const pharmacistDisplayName =
              appt.pharmacist ||
              servicePharmacists.find(
                (p: any) => String(p.id) === String(appt.resourceId || ""),
              )?.name ||
              (appt as any).pharmacistName ||
              (appt as any).resourceName ||
              "";

            return (
              <Card
                key={apptId}
                className={
                  isDarkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Header badge row */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <Badge
                          className={`text-xs ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-700"}`}
                        >
                          {appt.serviceType || `Follow-up ${index + 1}`}
                        </Badge>
                      </div>

                      {isEditing && editingAppt ? (
                        /* ════════ EDIT MODE ════════ */
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1.5">
                              <Label
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                              >
                                Service Type
                              </Label>
                              <Select
                                value={editingAppt.serviceId || ""}
                                onValueChange={handleEditServiceChange}
                                disabled={isEventCalendarLoading}
                              >
                                <SelectTrigger
                                  className={`h-8 text-sm ${isDarkMode ? "bg-gray-900 border-gray-600" : ""}`}
                                >
                                  <SelectValue placeholder="Select service" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(serviceTypesInfo || [])
                                    .filter(
                                      (s: any) =>
                                        s.serviceType !== 1 &&
                                        s.serviceType !== 3,
                                    )
                                    .map((svc: any) => (
                                      <SelectItem key={svc._id} value={svc._id}>
                                        {svc.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1.5">
                              <Label
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                              >
                                Pharmacist
                              </Label>
                              <Select
                                value={editingAppt.pharmacistId || ""}
                                onValueChange={handleEditPharmacistChange}
                                disabled={
                                  !editingAppt.serviceId ||
                                  isEventCalendarLoading
                                }
                              >
                                <SelectTrigger
                                  className={`h-8 text-sm ${isDarkMode ? "bg-gray-900 border-gray-600" : ""}`}
                                >
                                  <SelectValue placeholder="Select pharmacist" />
                                </SelectTrigger>
                                <SelectContent>
                                  {servicePharmacists.map((p: any) => (
                                    <SelectItem key={p.id} value={p.id}>
                                      {p.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1.5">
                              <Label
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                              >
                                Date
                              </Label>
                              <Input
                                type="date"
                                value={editingAppt.date || ""}
                                onChange={(e) =>
                                  handleEditDateChange(e.target.value)
                                }
                                min={todayStr}
                                disabled={
                                  !editingAppt.pharmacistId ||
                                  isEventCalendarLoading
                                }
                                className={`h-8 text-sm ${isDarkMode ? "bg-gray-900 border-gray-600" : ""}`}
                              />
                              {!!editApptDateError && (
                                <p className="text-xs text-red-500">
                                  {editApptDateError}
                                </p>
                              )}
                            </div>
                            <div className="space-y-1.5">
                              <Label
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                              >
                                Time
                              </Label>
                              <Select
                                value={editingAppt.time || ""}
                                onValueChange={handleEditTimeChange}
                                disabled={
                                  !editingAppt.date || isEventCalendarLoading
                                }
                              >
                                <SelectTrigger
                                  className={`h-8 text-sm ${isDarkMode ? "bg-gray-900 border-gray-600" : ""}`}
                                >
                                  <SelectValue placeholder="Select time" />
                                </SelectTrigger>
                                <SelectContent>
                                  {editApptAvailableTimes.map((t) => (
                                    <SelectItem key={t} value={t}>
                                      {t}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1.5">
                              <Label
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                              >
                                Duration
                              </Label>
                              <Select
                                value={editingAppt.duration || "30"}
                                onValueChange={(v) => updateEdit("duration", v)}
                              >
                                <SelectTrigger
                                  className={`h-8 text-sm ${isDarkMode ? "bg-gray-900 border-gray-600" : ""}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {durationOptions.map((d: any) => (
                                    <SelectItem key={d.value} value={d.value}>
                                      {d.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1.5">
                              <Label
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                              >
                                Room
                              </Label>
                              <Select
                                value={editingAppt.roomId || "none"}
                                onValueChange={(v) =>
                                  handleEditRoomChange(v === "none" ? "" : v)
                                }
                                disabled={
                                  !editingAppt.time || isEventCalendarLoading
                                }
                              >
                                <SelectTrigger
                                  className={`h-8 text-sm ${isDarkMode ? "bg-gray-900 border-gray-600" : ""}`}
                                >
                                  <SelectValue placeholder="Select room" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">
                                    No room assigned
                                  </SelectItem>
                                  {(serviceRooms as any[]).map((r: any) => (
                                    <SelectItem key={r.id} value={r.id}>
                                      {r.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label
                              className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              Notes
                            </Label>
                            <Textarea
                              value={editingAppt.notes || ""}
                              onChange={(e) =>
                                updateEdit("notes", e.target.value)
                              }
                              className={`text-sm ${isDarkMode ? "bg-gray-900 border-gray-600" : ""}`}
                              rows={2}
                            />
                          </div>

                          <div className="flex items-center gap-2 pt-2">
                            <Button
                              size="sm"
                              onClick={handleSaveEdit}
                              className="bg-black hover:bg-gray-800 text-white h-7 text-xs"
                            >
                              <Check className="w-3.5 h-3.5 mr-1" /> Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelEdit}
                              className={`h-7 text-xs ${isDarkMode ? "border-gray-600" : ""}`}
                            >
                              <X className="w-3.5 h-3.5 mr-1" /> Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* ════════ VIEW MODE ════════ */
                        <div className="space-y-1">
                          <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-gray-500" />
                              <span
                                className={
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }
                              >
                                {appt.date
                                  ? new Date(appt.date).toLocaleDateString(
                                      "en-GB",
                                      {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      },
                                    )
                                  : "—"}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-gray-500" />
                              <span
                                className={
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }
                              >
                                {appt.time
                                  ? appt.time
                                  : appt.startDate
                                    ? appt.startDate.substring(11, 16)
                                    : "—"}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-gray-500" />
                              <span
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }
                              >
                                {appt.duration} min
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5 text-gray-500" />
                              <span
                                className={
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }
                              >
                                {pharmacistDisplayName || "—"}
                              </span>
                            </div>
                            {appt.room && (
                              <div className="flex items-center gap-1.5">
                                <MapPin className="w-3.5 h-3.5 text-gray-500" />
                                <span
                                  className={
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-600"
                                  }
                                >
                                  {appt.room}
                                </span>
                              </div>
                            )}
                          </div>
                          {appt.notes && (
                            <p
                              className={`text-xs mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              {appt.notes}
                            </p>
                          )}
                          <div className="pt-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleStartEdit(appt)}
                              className={`h-7 text-xs ${isDarkMode ? "text-gray-400 hover:text-gray-300 hover:bg-gray-800" : "text-gray-600"}`}
                            >
                              <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                              Details
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* ── Tasks (only shown outside edit mode) ── */}
                      {!isEditing && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <ListTodo className="w-4 h-4 text-gray-500" />
                              <span
                                className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                              >
                                Tasks
                              </span>
                              {(appt.tasksToComplete?.length ?? 0) > 0 && (
                                <Badge
                                  variant="outline"
                                  className={`text-xs ${isDarkMode ? "border-gray-600 text-gray-400" : ""}`}
                                >
                                  {appt.tasksToComplete!.length}
                                </Badge>
                              )}
                            </div>
                            {addingTaskFor !== apptId && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setAddingTaskFor(apptId)}
                                className={`h-7 text-xs ${isDarkMode ? "text-gray-400 hover:text-gray-300 hover:bg-gray-800" : "text-gray-600"}`}
                              >
                                <Plus className="w-3.5 h-3.5 mr-1" /> Add Task
                              </Button>
                            )}
                          </div>
                          {(appt.tasksToComplete || []).map((taskText, ti) => (
                            <div
                              key={ti}
                              className={`flex items-center justify-between gap-2 p-2 rounded mb-1 ${isDarkMode ? "bg-gray-900/50" : "bg-gray-50"}`}
                            >
                              <span
                                className={`text-sm flex-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                              >
                                • {taskText}
                              </span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveTask(appt, taskText)}
                                className="h-6 w-6 p-0 text-red-500"
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          ))}
                          {addingTaskFor === apptId && (
                            <div className="flex items-center gap-2 mt-2">
                              <Input
                                placeholder="Type task and press Enter..."
                                value={newTaskText}
                                onChange={(e) => setNewTaskText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleAddTask(appt);
                                  } else if (e.key === "Escape") {
                                    setAddingTaskFor(null);
                                    setNewTaskText("");
                                  }
                                }}
                                autoFocus
                                className={`text-sm h-8 ${isDarkMode ? "bg-gray-900 border-gray-600 text-gray-200" : ""}`}
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setAddingTaskFor(null);
                                  setNewTaskText("");
                                }}
                                className="h-8"
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Vaccines (only shown outside edit mode) ── */}
                      {!isEditing && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Syringe className="w-4 h-4 text-gray-500" />
                              <span
                                className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                              >
                                Vaccines
                              </span>
                              {(appt.vaccinesAdminister?.length ?? 0) > 0 && (
                                <Badge
                                  variant="outline"
                                  className={`text-xs ${isDarkMode ? "border-gray-600 text-gray-400" : ""}`}
                                >
                                  {appt.vaccinesAdminister!.length}
                                </Badge>
                              )}
                            </div>
                            {vaccineModalFor !== apptId && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setVaccineModalFor(apptId);
                                  setVaccineInputValue("");
                                }}
                                className={`h-7 text-xs ${isDarkMode ? "text-gray-400 hover:text-gray-300 hover:bg-gray-800" : "text-gray-600"}`}
                              >
                                <Plus className="w-3.5 h-3.5 mr-1" /> Add
                                Vaccine
                              </Button>
                            )}
                          </div>
                          {vaccineModalFor === apptId && (
                            <div
                              className={`mb-2 p-2 rounded-md border ${
                                isDarkMode
                                  ? "bg-gray-900/60 border-gray-700"
                                  : "bg-gray-50 border-gray-200"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <div className="flex-1 min-w-0">
                                  <VaccineCombobox
                                    value={vaccineInputValue}
                                    onChange={setVaccineInputValue}
                                    placeholder="Search vaccine..."
                                    isDarkMode={isDarkMode}
                                    className="w-full"
                                    onSelect={(id) =>
                                      handleVaccineSelect(id, appt)
                                    }
                                  />
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    if (vaccineInputValue.trim()) {
                                      handleVaccineSelect(
                                        vaccineInputValue,
                                        appt,
                                      );
                                    }
                                  }}
                                  className="h-8 bg-black hover:bg-gray-800 text-white"
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setVaccineModalFor(null);
                                    setVaccineInputValue("");
                                  }}
                                  className="h-8"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          )}
                          {(appt.vaccinesAdminister || []).map((v: any) => (
                            <div
                              key={v.id}
                              className={`flex items-start justify-between gap-2 p-2 rounded mb-1 ${isDarkMode ? "bg-gray-900/50" : "bg-gray-50"}`}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Syringe className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                  <span
                                    className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                  >
                                    {v.name || v.vaccineName}
                                  </span>
                                </div>
                                {v.dose && (
                                  <p
                                    className={`text-xs ml-5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                  >
                                    Dose: {v.dose}
                                  </p>
                                )}
                                {v.notes && (
                                  <p
                                    className={`text-xs ml-5 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                                  >
                                    {v.notes}
                                  </p>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveVaccine(appt, v.id)}
                                className="h-6 w-6 p-0 text-red-500"
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          ))}
                          {(!appt.vaccinesAdminister ||
                            appt.vaccinesAdminister.length === 0) &&
                            vaccineModalFor !== apptId && (
                              <p
                                className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                              >
                                No vaccines assigned yet
                              </p>
                            )}
                        </div>
                      )}
                    </div>

                    {/* Delete button */}
                    {editMode ? (
                      /* ── EDIT MODE: confirm then hit API ── */
                      <>
                        {appt.eventStatus === 1 ||
                        (editMode &&
                          stateConsultationId &&
                          consultationStatus === 1) ? (
                          <div
                            title={
                              appt.eventStatus === 1
                                ? "This appointment is confirmed and cannot be deleted"
                                : "Cannot delete in active consultation"
                            }
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled
                              className={
                                isDarkMode
                                  ? "text-gray-600 hover:text-gray-600 cursor-not-allowed"
                                  : "text-gray-400 hover:bg-transparent cursor-not-allowed"
                              }
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={
                                  isDarkMode
                                    ? "text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                    : "text-red-600 hover:bg-red-50"
                                }
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Delete Appointment
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete{" "}
                                  <span className="font-semibold">
                                    {appt.serviceType ||
                                      `Follow-up ${index + 1}`}
                                  </span>{" "}
                                  on {appt.date} at {appt.time}? This will
                                  cancel the scheduled event and update the
                                  treatment plan.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={async () => {
                                    await onDeleteInEditMode?.(apptId);
                                  }}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </>
                    ) : (
                      /* ── ADD MODE: direct remove, no dialog ── */
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={
                          editMode &&
                          stateConsultationId &&
                          consultationStatus === 1
                        }
                        onClick={() => removeFollowUpAppointment(apptId)}
                        className={
                          isDarkMode
                            ? "text-red-400 hover:text-red-300 hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-red-400"
                            : "text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-red-600"
                        }
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {followUpAppointments.length > 0 && !isAdding && (
        <div
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
        >
          <p>
            {followUpAppointments.length} appointment
            {followUpAppointments.length !== 1 ? "s" : ""} scheduled
          </p>
        </div>
      )}
    </div>
  );
};
